import React, { useContext, useEffect, useRef, useState } from "react";
import Button from "components/BaseWidgets/button";
import "./style.scss";
import Slider from "components/BaseWidgets/slider";
import Tag from "components/BaseWidgets/tag";
import {
  FILTER_QUERY_PARAMS,
  getFilterData,
  NON_FILTER_QUERY_PARAMS,
} from "components/Explore/helper";
import Accordion from "components/BaseWidgets/accordion";
import { ExploreContext } from "context/ExploreContext";
import { FilterIcon } from "assets/icons";
import {
  useCourses,
  useDistrict,
  useIncomes,
  useProfession,
  useSelectOptions,
  useStates,
  useUserCommunities,
} from "hooks/api/useListing";
import { useLocation, useNavigate } from "react-router-dom";
import { usePremium } from "context/PremiumContext";
import GoPremiumCard from "components/CustomWidgets/goPremiumCard";
import { retrieveToken } from "utils/storage";
import { useOnboardingDetails } from "hooks/api/useOnboard";

const Filter = ({ className = "", show = false, setShowFilter }) => {
  const [partnerData, setPartnerData] = useState({});
  const [defaultCities, setDefaultCities] = useState([]);
  const { mutateAsync: fetchOnboardingDetails } = useOnboardingDetails();

  const userGender = JSON.parse(retrieveToken("user"));
  const gender = userGender?.gender;
  const dummyData = getFilterData(gender);
  const navigate = useNavigate();
  const location = useLocation();
  const prevPathRef = useRef(null);
  const {
    selectedTags,
    setSelectedTags,
    sliderValues,
    setSliderValues,
    expandedTags,
    setExpandedTags,
    setFilterData,
    filterData,
    clearAll,
  } = useContext(ExploreContext);
  const { isPremium } = usePremium();
  const filterHistory = useRef([]);
  const isPopState = useRef(false);
  const isInitialMount = useRef(true);

  const ALL_FILTER_PARAMS = [...FILTER_QUERY_PARAMS];

  const {
    nationality,
    maritalStatus,
    familyStatus,
    bodyType,
    qualification,
    familyType,
    complexion,
    diet,
    motherTongue,
  } = useSelectOptions();

  const formatIds = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return items.length === 1 ? items[0].id : items.map((item) => item.id);
  };

  const selectedNationalityId = formatIds(selectedTags?.nationality);
  const selectedStateId = formatIds(selectedTags?.state);

  const { data: stateData } = useStates({
    search: "",
    country_id: selectedNationalityId || partnerData?.country,
    enabled: !!selectedNationalityId,
  });
  const { data: districtData } = useDistrict({
    search: "",
    parent_id: selectedStateId || partnerData?.city,
    enabled: !!selectedStateId,
  });
  const { data: professionData } = useProfession({
    search: "",
  });
  const { data: incomeData } = useIncomes({
    search: "",
  });
  const { data: communityData } = useUserCommunities({
    search: "",
  });
  const selectedQualificationId = formatIds(selectedTags?.qualification);

  const { data: courseData, isLoading: isCoursePending } = useCourses({
    search: "",
    parent_id: selectedQualificationId,
  });

  useEffect(() => {
    if (!districtData || !partnerData?.place?.length) return;

    const defaultCities = Object.values(districtData).filter((item) =>
      partnerData?.place.includes(item.id),
    );
    const cityNames = defaultCities.map((city) => city.name);
    setDefaultCities(cityNames);
  }, [districtData]);

  useEffect(() => {
    // Skip clear if coming from /person-profile
    if (prevPathRef.current !== "/person-profile") {
      clearAll();
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  const dynamicSources = {
    annual_income: incomeData,
    community: communityData,
    nationality: nationality?.options,
    state: stateData,
    city: districtData,
    marital_status: maritalStatus?.options,
    family_status: familyStatus?.options,
    family_type: familyType?.options,
    body_type: bodyType?.options,
    qualification: qualification?.options,
    profession: professionData,
    complexion: complexion?.options,
    diet: diet?.options,
    motherTongue: motherTongue?.options,
    education: courseData,
  };

  const allowedFiltersForFree = ["age", "height", "city"];

  const dynamicFilterData = dummyData
    .filter((item) => {
      // If premium, show all filters
      if (isPremium) return true;
      // If not premium, only show allowed filters
      return allowedFiltersForFree.includes(item.id);
    })
    .map((item) => {
      const source = dynamicSources[item.id];
      if (source && source.length) {
        return {
          ...item,
          tags: source.map((c) => ({
            id: c.id || c.value,
            label: c.name || c.label,
          })),
        };
      }
      return item;
    });

  const tagLookup = {};
  dummyData?.forEach((filter) => {
    if (filter.tags?.length) {
      filter.tags.forEach((tag) => {
        tagLookup[`${filter.id}-${tag.id}`] = tag.label;
      });
    }
  });

  // 2. Add dynamic tags from dynamicSources (overrides or supplements static)
  Object.entries(dynamicSources)?.forEach(([filterId, source]) => {
    if (!source) return;
    const items = Array.isArray(source) ? source : source.options || [];
    items?.forEach((item) => {
      const id = item.id || item.value;
      const label = item.name || item.label;
      if (id !== undefined) {
        tagLookup[`${filterId}-${id}`] = label;
      }
    });
  });

  // Re-sync labels when tagLookup updates (e.g. after courses load)
  useEffect(() => {
    if (Object.keys(tagLookup).length === 0) return;

    setSelectedTags((prev) => {
      let changed = false;
      const next = { ...prev };

      Object.entries(next).forEach(([filterId, tags]) => {
        if (!Array.isArray(tags)) return;

        const updatedTags = tags.map((tag) => {
          const lookupKey = `${filterId}-${tag.id}`;
          const correctLabel = tagLookup[lookupKey];

          if (correctLabel && tag.label !== correctLabel) {
            changed = true;
            return { ...tag, label: correctLabel };
          }
          return tag;
        });

        if (changed) {
          next[filterId] = updatedTags;
        }
      });

      return changed ? next : prev;
    });
  }, [tagLookup]);

  const syncStateFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const newSelectedTags = {};
    const newSliderValues = {};

    params.forEach((value, key) => {
      if (NON_FILTER_QUERY_PARAMS.includes(key)) return;

      if (["age", "height", "weight"].includes(key)) {
        const [min, max] = value.replace(/cm|Kg/g, "").split("-").map(Number);
        newSliderValues[key] = [min, max];
      } else if (ALL_FILTER_PARAMS.includes(key)) {
        const filterType = dynamicFilterData.find((f) => f.id === key)?.type;
        const parsedValue = isNaN(Number(value)) ? value : Number(value);
        const tagObj = {
          id: parsedValue,
          label: tagLookup[`${key}-${value}`] || value,
        };
        if (filterType === "select") {
          // always overwrite with single value for select
          newSelectedTags[key] = [tagObj];
        } else {
          if (!newSelectedTags[key]) newSelectedTags[key] = [];
          newSelectedTags[key].push(tagObj);
        }
      }
    });

    setSelectedTags((prev) => {
      const merged = { ...prev, ...newSelectedTags };
      Object.keys(merged).forEach((k) => {
        if (!params.has(k) && ALL_FILTER_PARAMS.includes(k)) {
          merged[k] = [];
        }
      });
      return merged;
    });

    setSliderValues((prev) => ({ ...prev, ...newSliderValues }));

    // Build API payload with correct format
    const apiFilterData = {};
    Object.entries(newSelectedTags).forEach(([key, values]) => {
      if (values.length > 0) {
        const filterType = dynamicFilterData.find((f) => f.id === key)?.type;
        if (filterType === "select") {
          apiFilterData[key] = values[0].id; // single value
        } else {
          apiFilterData[key] = values.map((v) => v.id); // array for tags
        }
      }
    });

    Object.entries(newSliderValues).forEach(([key, range]) => {
      if (Array.isArray(range)) {
        apiFilterData[key] = `${range[0]}-${range[1]}`;
      }
    });

    setFilterData(apiFilterData);
  };

  // Update URL when state changes
  const updateUrlFromState = () => {
    const params = new URLSearchParams(location.search);
    const newParams = new URLSearchParams();

    // Preserve all non-filter parameters
    NON_FILTER_QUERY_PARAMS.forEach((param) => {
      const value = params.get(param);
      if (value) {
        newParams.set(param, value);
      }
    });

    // Add selected tags to URL (only filter parameters)
    Object.entries(selectedTags).forEach(([key, values]) => {
      if (ALL_FILTER_PARAMS.includes(key) && values?.length > 0) {
        values.forEach((v) => newParams.append(key, v.id));
      }
    });

    // Add slider values to URL
    Object.entries(sliderValues).forEach(([key, range]) => {
      if (
        Array.isArray(range) &&
        range.length === 2 &&
        ALL_FILTER_PARAMS.includes(key)
      ) {
        if (key === "age") {
          newParams.set(key, `${range[0]}-${range[1]}`);
        } else if (key === "height") {
          newParams.set(key, `${range[0]}cm-${range[1]}cm`);
        } else if (key === "weight") {
          newParams.set(key, `${range[0]}Kg-${range[1]}Kg`);
        }
      }
    });

    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    syncStateFromUrl();
  }, [location.search]);

  useEffect(() => {
    updateUrlFromState();
  }, [selectedTags, sliderValues]);

  useEffect(() => {
    const handlePopState = () => {
      isPopState.current = true;
      // Remove current URL from history
      filterHistory.current.pop();
      // Get previous URL
      const prevUrl = filterHistory.current.at(-1);
      if (prevUrl) {
        const searchParams = new URLSearchParams(prevUrl.split("?")[1]);
        const restoredFilters = {};
        // Restore filters from URL (only filter parameters)
        searchParams.forEach((value, key) => {
          if (ALL_FILTER_PARAMS.includes(key)) {
            if (!restoredFilters[key]) restoredFilters[key] = [];
            restoredFilters[key].push(value);
          }
        });
        // Update selected tags (only for filter parameters)
        setSelectedTags((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((k) => {
            if (ALL_FILTER_PARAMS.includes(k)) {
              if (restoredFilters[k]) {
                updated[k] = restoredFilters[k].map((id) => ({
                  id,
                  label: tagLookup[`${k}-${id}`] || id,
                }));
              } else {
                updated[k] = [];
              }
            }
          });
          return updated;
        });
        // Update slider values
        setSliderValues((prev) => {
          const updated = { ...prev };
          Object.keys(restoredFilters).forEach((key) => {
            if (["age", "height", "weight"].includes(key)) {
              const [min, max] = restoredFilters[key][0]
                .replace(/cm|Kg/g, "")
                .split("-")
                .map(Number);
              updated[key] = [min, max];
            }
          });
          return updated;
        });
      } else {
        // If no history left, reset only filter parameters
        setSelectedTags((prev) => {
          const updated = { ...prev };
          ALL_FILTER_PARAMS.forEach((param) => {
            if (updated[param]) {
              updated[param] = [];
            }
          });
          return updated;
        });
        setSliderValues({});
      }
      setTimeout(() => {
        isPopState.current = false;
      }, 100);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Handle initial mount settling
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 500); // 500ms delay to allow URL sync and dynamic data to settle
    return () => clearTimeout(timer);
  }, []);

  // Stabilize dependencies for clearing effects
  const nationalityDep = JSON.stringify(selectedNationalityId);
  const stateDep = JSON.stringify(selectedStateId);
  const qualificationDep = JSON.stringify(selectedQualificationId);

  useEffect(() => {
    if (isInitialMount.current || isPopState.current) return;
    setSelectedTags((prev) => ({
      ...prev,
      state: [],
      city: [],
    }));
  }, [nationalityDep]);

  useEffect(() => {
    if (isInitialMount.current || isPopState.current) return;
    setSelectedTags((prev) => ({
      ...prev,
      city: [],
    }));
  }, [stateDep]);

  useEffect(() => {
    if (isInitialMount.current || isPopState.current) return;
    setSelectedTags((prev) => ({
      ...prev,
      education: [],
    }));
  }, [qualificationDep]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const newFilterData = {};
      Object.entries(selectedTags).forEach(([key, values]) => {
        if (ALL_FILTER_PARAMS.includes(key) && values && values.length > 0) {
          const filterType = dynamicFilterData.find((f) => f.id === key)?.type;
          if (filterType === "select") {
            // Only take the first (single-value select)
            newFilterData[key] = isNaN(Number(values[0].id))
              ? values[0].id
              : Number(values[0].id);
          } else {
            // Tags and multi-select filters → array
            newFilterData[key] = values.map((v) =>
              isNaN(Number(v.id)) ? v.id : Number(v.id)
            );
          }
        }
      });
      // Add slider values
      Object.entries(sliderValues).forEach(([key, range]) => {
        if (
          Array.isArray(range) &&
          range.length === 2 &&
          ALL_FILTER_PARAMS.includes(key)
        ) {
          if (key === "age") {
            newFilterData[key] = `${range[0]}-${range[1]}`;
          } else if (key === "height") {
            newFilterData[key] = `${range[0]}cm-${range[1]}cm`;
          } else if (key === "weight") {
            newFilterData[key] = `${range[0]}Kg-${range[1]}Kg`;
          }
        }
      });
      setFilterData(newFilterData);
    }, 500);

    return () => clearTimeout(handler);
  }, [selectedTags, sliderValues, setFilterData]);

  const toggleTag = (filterId, tagId, tagLabel) => {
    setSelectedTags((prev) => {
      const currentTags = prev[filterId] || [];
      const tagExists = currentTags.find((t) => t.id === tagId);
      let updatedTags;
      if (tagExists) {
        updatedTags = currentTags.filter((t) => t.id !== tagId);
      } else {
        updatedTags = [
          ...currentTags,
          { id: tagId, label: tagLookup[`${filterId}-${tagId}`] || tagLabel },
        ];
      }
      return {
        ...prev,
        [filterId]: updatedTags,
      };
    });
  };

  const handleSliderChange = (filterId, value) => {
    setSliderValues((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  };

  const toggleExpand = (filterId) => {
    setExpandedTags((prev) => ({
      ...prev,
      [filterId]: !prev[filterId],
    }));
  };

  const toggleSelect = (filterId, tagId, tagLabel) => {
    setSelectedTags((prev) => {
      const currentTags = prev[filterId] || [];
      const isSelected = currentTags.some((t) => t.id === tagId);
      let updatedTags;
      if (isSelected) {
        updatedTags = [];
      } else {
        updatedTags = [
          { id: tagId, label: tagLookup[`${filterId}-${tagId}`] || tagLabel },
        ];
      }
      return {
        ...prev,
        [filterId]: updatedTags,
      };
    });
  };

  const lockedFilters = dummyData.filter(
    (item) => !allowedFiltersForFree.includes(item.id)
  );

  useEffect(() => {
    const getDetails = async () => {
      try {
        const response = await fetchOnboardingDetails({ step: 6 });
        setPartnerData(response.partner_preference);
      } catch (error) {
        console.error("Error fetching onboarding details:", error);
      }
    };

    getDetails();
  }, []);

  // load query params for chheck  default filters

  useEffect(() => {
    // don’t apply defaults on /engage
    // if (location.pathname.startsWith("/s/")) return;
    if (!location.pathname.startsWith("/explore")) return;

    // wait until BOTH partnerData and defaultCities are ready
    if (!partnerData?.age || !partnerData.age.includes("-")) return;
    if (!defaultCities.length && partnerData.place?.length) return;

    const [min, max] = partnerData.age.split("-").map(Number);

    setSliderValues((prev) => ({
      ...prev,
      age: [min, max],
      // city: defaultCities,
    }));

    setSelectedTags((prev) => ({
      ...prev,
      city: defaultCities.map((city, index) => ({
        id: partnerData.place[index],
        label: city,
      })),
    }));

    setFilterData((prev) => ({
      ...prev,
      age: `${min}-${max}`,
      // nationality: partnerData?.country,
      // state: partnerData?.city,
      // city: partnerData?.place,
    }));
  }, [partnerData, defaultCities, location.pathname]);

  return (
    <div
      className={`filter-container ${className} ${show ? "visible" : "hidden"}`}
    >
      <div className="flex items-center gap-2 title-container justify-between">
        <div className="filter-head flex items-center gap-2">
          <FilterIcon />
          <h2 className="section-title font-title-normal">Filters</h2>
        </div>
        <div
          className="mob-view-closer"
          onClick={() => setShowFilter && setShowFilter(false)}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="32" height="32" rx="16" fill="#F2F2F2"></rect>
            <path
              d="M20.523 11.6367L11.1514 21.0083"
              stroke="#1C1D1D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path
              d="M20.523 21.0122L11.1514 11.6406"
              stroke="#1C1D1D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
          </svg>
        </div>
      </div>
      <Accordion
        accordionClassName="filter-accordion"
        data={dynamicFilterData
          ?.filter((item) => !item?.hidden)
          ?.map((item) => ({
            key: item?.id,
            label: item?.label,
            children: (
              <>
                {item?.type === "slider" && (
                  <Slider
                    variant={item.id}
                    minValue={item.slider.min}
                    maxValue={item.slider.max}
                    defaultValue={item.slider.defaultValue}
                    value={sliderValues[item.id] || item.slider.defaultValue}
                    onChange={(val) => handleSliderChange(item.id, val)}
                  />
                )}

                {item.type === "tags" && (
                  <>
                    {item.tags.length === 0 ? (
                      <p className="no-data-text">No data found!</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-3">
                          {(expandedTags[item.id]
                            ? item.tags
                            : item.tags.slice(0, 5)
                          ).map((tag) => {
                            const isSelected = selectedTags[item.id]?.some(
                              (t) => t.id === tag.id
                            );
                            return (
                              <Tag
                                className="filter-tag"
                                key={tag.id}
                                onClick={() =>
                                  toggleTag(item.id, tag.id, tag.label)
                                }
                                isActive={isSelected}
                              >
                                {tag.label}
                              </Tag>
                            );
                          })}
                        </div>
                        {item.tags.length > 5 && (
                          <Button
                            className="view-more-btn"
                            variant="link"
                            onClick={() => toggleExpand(item.id)}
                          >
                            {expandedTags[item.id] ? "View less" : "View more"}
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            ),
          }))}
      />

      {/* Locked Filters with Blur Effect */}
      {!isPremium && lockedFilters.length > 0 && (
        <div className="locked-filters-wrapper">
          <div className="locked-filters-content">
            <Accordion
              accordionClassName="filter-accordion blurred"
              data={lockedFilters.map((item) => ({
                key: item.id,
                label: item.label,
                children: (
                  <div className="filter-placeholder">
                    {item.type === "slider" && (
                      <div className="slider-placeholder" />
                    )}
                    {item.type === "tags" && (
                      <div className="flex flex-wrap gap-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="tag-placeholder" />
                        ))}
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
          </div>
          <div className="premium-card-overlay">
            <GoPremiumCard
              title="To unlock more search filters"
              outlineBlur
              className="blur-card"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Filter;
